import AHDate from "_shared/dates/date";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import FeeScheduleSchema, {
    FeeScheduleJSON,
    FeeScheduleParams,
} from "work/fee-schedules/_schemas/fee-schedule-schema";

type FeeScheduleCategoryType = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;

export default class FeeSchedule {
    public static get EXAMPLE(): FeeSchedule {
        return new FeeSchedule({
            workAgreementId: Guid.generate(),
            categories: [FeeScheduleCategory.EXAMPLE],
            clientFeeScheduleTemplateIds: [Guid.generate(), Guid.generate()],
            vendorFeeScheduleTemplateIds: [Guid.generate(), Guid.generate()],
            hireDate: new AHDate(),
        });
    }

    public static create(params: FeeScheduleParams): FeeSchedule {
        const parsed = FeeScheduleSchema.validate(params);
        return new FeeSchedule(parsed);
    }

    public static fromJSON(json: FeeScheduleJSON): FeeSchedule {
        const params = FeeScheduleSchema.deserialize(json);
        return new FeeSchedule(params);
    }

    public static fromJSONArray(jsonArray: FeeScheduleJSON[]): FeeSchedule[] {
        return jsonArray.map((json) => FeeSchedule.fromJSON(json));
    }

    public readonly workAgreementId: Guid;
    public readonly hireDate?: AHDate;
    public readonly archiveDate?: AHDate;

    public get categories(): FeeScheduleCategoryType[] {
        return [...this._categories];
    }

    public get clientFeeScheduleTemplateIds(): Guid[] {
        return [...this._clientFeeScheduleTemplateIds];
    }

    public get vendorFeeScheduleTemplateIds(): Guid[] {
        return [...this._vendorFeeScheduleTemplateIds];
    }

    public get isActive(): boolean {
        return Boolean(this.hireDate && this.hireDate.isSameOrBefore(new AHDate()) && !this.archiveDate);
    }

    public get isArchived(): boolean {
        return Boolean(this.archiveDate);
    }

    public constructor(params: FeeScheduleParams) {
        this.workAgreementId = params.workAgreementId;
        this.hireDate = params.hireDate;
        this.archiveDate = params.archiveDate;
        this._categories =
            params.categories?.map((category) =>
                category instanceof FeeScheduleCategory
                    ? category.clone()
                    : FeeScheduleCategoryMaskedCurrency.create(category)
            ) ?? [];
        this._clientFeeScheduleTemplateIds = params.clientFeeScheduleTemplateIds?.map((id) => id.clone()) ?? [];
        this._vendorFeeScheduleTemplateIds = params.vendorFeeScheduleTemplateIds?.map((id) => id.clone()) ?? [];
    }

    public toJSON(): FeeScheduleJSON {
        return FeeScheduleSchema.serialize(this);
    }

    public isEqualTo(other: FeeSchedule | null | undefined): boolean {
        if (!other) return false;
        return this.workAgreementId.isEqualTo(other.workAgreementId);
    }

    private readonly _categories: FeeScheduleCategoryType[];
    private readonly _clientFeeScheduleTemplateIds: Guid[];
    private readonly _vendorFeeScheduleTemplateIds: Guid[];
}

